import { ReactElement, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CalendarIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  CirclePlusIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { z } from "zod";

import { SheetResourcesWrapper } from "./SheetResourcesWrapper";

import {
  Column,
  LocationInput,
  RichTextEditorInput,
  Row,
  Text,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { WorkExperience } from "./WorkExperience";
import { WorkExperiencesView } from "./WorkExperiencesView";

import { useWorkExperience } from "@/hooks/mutations/useWorkExperience";
import {
  CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME,
  compensationTypeOptions,
  monthOptions,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { locations } from "@/lib/locations";
import { novaClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { CompensationType, InternalCompany, WorkExperienceType } from "@/types";

const workExperiencesTitle = "Work Experience";
const EARLIEST_WORK_EXPERIENCE_YEAR = 1950;
const FUTURE_WORK_EXPERIENCE_YEAR_BUFFER = 5;

const workExperienceSchema = z
  .object({
    title: z.string().min(1, { message: "Required" }),
    internalCompany: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      // Internal company may be null if a work experience from a parsed resume was missing company name
      .optional(),
    location: z
      .string()
      .refine(
        (location) =>
          locations.validateLocation({ enforceFullLocation: false, location }),
        {
          message:
            "Must be City, State, USA with a valid state code (e.g Knoxville, TN, USA)",
        },
      ),
    startMonth: z.string(),
    startYear: z.string().refine((data) => !isNaN(Number(data)), {
      message: "Year must be a valid integer",
    }),
    endMonth: z.string(),
    endYear: z.string().refine((data) => !isNaN(Number(data)), {
      message: "Year must be a valid integer",
    }),
    currentPosition: z.boolean(),
    descriptions: z.array(z.string()),
    startPay: z.preprocess(
      (x) => {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return x || null;
      },
      z.union([z.null(), z.coerce.number().min(1)]).optional(),
    ),
    finalPay: z.preprocess(
      (x) => {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return x || null;
      },
      z.union([z.null(), z.coerce.number().min(1)]).optional(),
    ),
    compensationType: z.nativeEnum(CompensationType, { message: "Required" }),
  })
  .superRefine((data, ctx) => {
    dates.validateDatesForForm(data, ctx, false);
  });

const WorkExperienceCompanyInput = ({
  fallbackName,
  form,
  isCompact = false,
  onSelect,
}: {
  fallbackName?: string;
  form: ReturnType<typeof useForm<z.infer<typeof workExperienceSchema>>>;
  isCompact?: boolean;
  onSelect?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 250);

  const utils = novaClient.useUtils();

  const { mutate: findFirstByNameOrCreateInternalCompany } =
    novaClient.internal.admin.company.getOrCreate.useMutation({
      onSuccess: (foundOrCreatedInternalCompany: InternalCompany) => {
        // Invalidate the company search cache so new company shows up
        void utils.internal.admin.company.findManyByName.invalidate();
        handleSelectCompany(foundOrCreatedInternalCompany);
      },
    });
  const { data: internalCompanies } =
    novaClient.internal.admin.company.findManyByName.useQuery(
      {
        name: debouncedQuery,
      },
      {
        staleTime: Infinity, // Makes this re-use cache
      },
    );

  const handleSelectCompany = (internalCompany: InternalCompany) => {
    form.setValue("internalCompany", internalCompany);
    setOpen(false);
    setQuery("");
    onSelect?.();
  };

  const handleCreateCompany = () => {
    if (!query.trim()) return;

    findFirstByNameOrCreateInternalCompany({
      name: query.trim(),
    });
  };

  return (
    <FormField
      control={form.control}
      name="internalCompany"
      render={({ field }) => {
        return (
          <FormItem
            className={cn("w-full text-slate-700", isCompact && "space-y-0")}
          >
            {isCompact ? null : <FormLabel>Company</FormLabel>}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    className={cn(
                      "w-full justify-between border-slate-200 bg-white px-3 font-normal text-slate-700 hover:bg-slate-50",
                      isCompact ? "h-8" : "h-9",
                    )}
                    role="combobox"
                    variant="outlineSecondary"
                  >
                    <Text className="truncate text-sm font-normal text-slate-700">
                      {field.value?.name || fallbackName || "Select company"}
                    </Text>
                    <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0"
              >
                <Command
                  shouldFilter={false} // IMPORTANT: Do not change this.
                >
                  <CommandInput
                    onValueChange={setQuery}
                    placeholder="Search companies..."
                    value={query}
                  />
                  <CommandList>
                    <CommandEmpty>No companies found.</CommandEmpty>
                    <CommandGroup>
                      {internalCompanies?.map((company: InternalCompany) => (
                        <CommandItem
                          key={company.id}
                          onSelect={() => {
                            handleSelectCompany(company);
                          }}
                        >
                          <CheckIcon
                            className={cn(
                              "mr-2 h-4 w-4 text-primary-700",
                              field.value?.id === company.id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {company.name}
                        </CommandItem>
                      ))}
                      {query && (
                        <CommandItem onSelect={handleCreateCompany}>
                          <PlusIcon className="h-4 w-4" />
                          Create &quot;{query}&quot;
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage className="mt-1 text-right text-[10px] font-medium text-red-500" />
          </FormItem>
        );
      }}
    />
  );
};

const formatDescription = (descriptions: string[]) => {
  if (!descriptions.length) {
    return "";
  }

  const listItems = descriptions
    .map((description) => {
      return `<li><p>${description}</p></li>`;
    })
    .join("");

  return `<ul>${listItems}</ul>`;
};

const parseDescription = (value: string): string[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(value, "text/html");
  const listItems = doc.body.querySelectorAll("li");
  return Array.from(listItems).map((item) => item.textContent ?? "");
};

const WorkExperienceDescriptionInput = ({
  form,
}: {
  form: ReturnType<typeof useForm<z.infer<typeof workExperienceSchema>>>;
}) => {
  return (
    <FormField
      control={form.control}
      name="descriptions"
      render={({ field }) => (
        <FormItem className="text-slate-700">
          <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>
            Description
          </Text>
          <FormControl>
            <RichTextEditorInput
              mode={{
                onChange: (value) => {
                  field.onChange(parseDescription(value));
                },
                type: "html",
                value: formatDescription(field.value),
              }}
              disableBold
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const WorkExperienceEdit = ({
  onDoneCreating,
  userId,
  workExperience,
}: {
  // Present in create mode; called after a successful create or on cancel.
  onDoneCreating?: () => void;
  userId: number;
  workExperience?: WorkExperienceType;
}) => {
  const isCreating = !workExperience;

  const [activeField, setActiveField] = useState("all");
  const editorRef = useRef<HTMLDivElement>(null);

  const {
    createWorkExperience,
    setSheetDataWorkExperiences,
    updateWorkExperience,
  } = useWorkExperience();

  // The mutation responses omit the internalCompany relation, so graft the
  // form's selection onto the cached row the hook-level sync wrote.
  const patchInternalCompany = (
    workExperienceId: number,
    internalCompany?: InternalCompany,
  ) => {
    if (!internalCompany?.id) return;

    setSheetDataWorkExperiences(userId, (workExperiences) =>
      workExperiences.map((cached) =>
        cached.id === workExperienceId
          ? { ...cached, internalCompany }
          : cached,
      ),
    );
  };

  const disabled =
    createWorkExperience.isPending || updateWorkExperience.isPending;

  const form = useForm<z.infer<typeof workExperienceSchema>>({
    defaultValues: {
      title: workExperience?.title ?? "",
      internalCompany: workExperience?.internalCompany ?? { id: "", name: "" },
      location: workExperience?.location ?? "",
      startMonth: workExperience?.startMonth ?? "",
      startYear: workExperience?.startYear ?? "",
      endMonth: workExperience?.endMonth ?? "",
      endYear: workExperience?.endYear ?? "",
      currentPosition: workExperience?.currentPosition ?? false,
      descriptions: workExperience?.descriptions ?? [],
      startPay: workExperience?.startPay
        ? Number(workExperience.startPay)
        : undefined,
      finalPay: workExperience?.finalPay
        ? Number(workExperience.finalPay)
        : undefined,
      compensationType:
        (workExperience?.compensationType as CompensationType) ||
        CompensationType.HOURLY,
    },
    resolver: zodResolver(workExperienceSchema),
  });

  const handleCreateWorkExperience = (
    data: z.infer<typeof workExperienceSchema>,
  ) => {
    const createRequest = {
      userId,
      title: data.title,
      internalCompanyId: data.internalCompany?.id,
      location: data.location || undefined,
      startMonth: data.startMonth || undefined,
      startYear: data.startYear || undefined,
      endMonth: data.endMonth || undefined,
      endYear: data.endYear || undefined,
      descriptions: data.descriptions,
      currentPosition: data.currentPosition,
      // TODO: Revisit how we handle numbers as part of zod schemas and forms
      startPay: data.startPay ? data.startPay.toString() : undefined,
      finalPay: data.finalPay ? data.finalPay.toString() : undefined,
      compensationType: data.compensationType,
    };

    // useWorkExperience updates the sheet-data cache at the hook level, so
    // success only patches the relation and manages local form state.
    createWorkExperience.mutate(
      {
        userId,
        data: createRequest,
      },
      {
        onSuccess: (created) => {
          patchInternalCompany(
            (created as WorkExperienceType).id,
            data.internalCompany as InternalCompany | undefined,
          );
          form.reset();
          onDoneCreating?.();
        },
      },
    );
  };

  const handleUpdateWorkExperience = (
    data: z.infer<typeof workExperienceSchema>,
  ) => {
    if (!workExperience) return;

    const updateRequest = {
      id: workExperience.id,
      userId,
      title: data.title,
      internalCompanyId: data.internalCompany?.id,
      location: data.location,
      startMonth: data.startMonth,
      startYear: data.startYear,
      endMonth: data.endMonth,
      endYear: data.endYear,
      currentPosition: data.currentPosition,
      descriptions: data.descriptions,
      startPay: data.startPay ? data.startPay.toString() : undefined,
      finalPay: data.finalPay ? data.finalPay.toString() : undefined,
      compensationType: data.compensationType as CompensationType,
    };

    updateWorkExperience.mutate(
      {
        userId,
        data: updateRequest,
      },
      {
        onSuccess: (updatedWorkExperience) => {
          patchInternalCompany(
            (updatedWorkExperience as WorkExperienceType).id,
            data.internalCompany as InternalCompany | undefined,
          );
          handleUpdateWorkExperienceSuccess({
            internalCompany: data.internalCompany as InternalCompany,
            updatedWorkExperience: updatedWorkExperience as WorkExperienceType,
          });
        },
      },
    );
  };

  const handleUpdateWorkExperienceSuccess = ({
    internalCompany,
    updatedWorkExperience,
  }: {
    internalCompany: InternalCompany;
    updatedWorkExperience: Omit<WorkExperienceType, "internalCompany">;
  }) => {
    form.reset({
      title: updatedWorkExperience.title ?? "",
      internalCompany: internalCompany,
      location: updatedWorkExperience.location ?? "",
      startMonth: updatedWorkExperience.startMonth ?? "",
      startYear: updatedWorkExperience.startYear ?? "",
      endMonth: updatedWorkExperience.endMonth ?? "",
      endYear: updatedWorkExperience.endYear ?? "",
      currentPosition: updatedWorkExperience.currentPosition ?? false,
      descriptions: updatedWorkExperience.descriptions ?? [],
      startPay: updatedWorkExperience.startPay
        ? Number(updatedWorkExperience.startPay)
        : undefined,
      finalPay: updatedWorkExperience.finalPay
        ? Number(updatedWorkExperience.finalPay)
        : undefined,
      compensationType:
        (updatedWorkExperience.compensationType as CompensationType) ||
        CompensationType.HOURLY,
    });
  };

  const handleCancelField = () => {
    form.reset();
    setActiveField("all");
  };

  const handleCancelCreate = () => {
    form.reset();
    onDoneCreating?.();
  };

  const handleEditField = (field: string) => {
    setActiveField(field);
  };

  const handleSave = () => {
    if (isCreating) {
      void form.handleSubmit(handleCreateWorkExperience)();
      return;
    }

    void form.handleSubmit(handleUpdateWorkExperience)();
    setActiveField("all");
  };

  useEffect(() => {
    // Creating saves only via the explicit controls — an outside click must
    // not fire a create with a half-filled form.
    if (isCreating) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement;
      const isInsideActiveField = target.closest("[data-inline-editor-active]");
      const isInsidePopover = target.closest(
        "[data-radix-popper-content-wrapper]",
      );

      if (activeField && !isInsideActiveField && !isInsidePopover) handleSave();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [activeField, handleSave]);

  const renderInlineField = ({
    content,
    editor,
    field,
    hasFloatingLabel = true,
    isContentHugging = false,
    label,
    tooltip,
  }: {
    content: React.ReactNode;
    editor: React.ReactNode;
    field: string;
    hasFloatingLabel?: boolean;
    isContentHugging?: boolean;
    label: string;
    tooltip: string;
  }) => {
    if (activeField === "all" || activeField === field) {
      if (!hasFloatingLabel) {
        return (
          <Row
            className={field === "title" ? "w-full" : undefined}
            data-inline-editor-active
          >
            {editor}
          </Row>
        );
      }

      return (
        <Column
          className="min-w-0 items-start gap-1 text-left"
          data-inline-editor-active
        >
          <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>
            {label}
          </Text>
          {editor}
        </Column>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Row
            className={cn(
              "-mx-2 cursor-text rounded-md px-2 py-1 transition-colors hover:bg-slate-100",
              isContentHugging ? "w-fit" : "w-full",
            )}
            onClick={() => {
              handleEditField(field);
            }}
          >
            {content}
          </Row>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") handleSave();
    if (event.key === "Escape") handleCancelField();
  };

  const startMonth = form.watch("startMonth");
  const startYear = form.watch("startYear");
  const endMonth = form.watch("endMonth");
  const endYear = form.watch("endYear");
  const locationValue = form.watch("location");
  const startPayValue = form.watch("startPay");
  const finalPayValue = form.watch("finalPay");
  const compensationTypeValue = form.watch("compensationType");
  const isCurrentPosition = form.watch("currentPosition");
  const dateDisplay =
    dates.prepareDateDisplayForCertificationOrEducationOrWorkExperience({
      startMonth,
      startYear,
      endMonth,
      endYear,
      current: isCurrentPosition,
    });
  const tenure =
    dates.calculateTenureForCertificationOrEducationOrWorkExperience({
      startMonth,
      startYear,
      endMonth,
      endYear,
      current: isCurrentPosition,
    });
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    {
      length:
        currentYear +
        FUTURE_WORK_EXPERIENCE_YEAR_BUFFER -
        EARLIEST_WORK_EXPERIENCE_YEAR +
        1,
    },
    (_, index) =>
      String(currentYear + FUTURE_WORK_EXPERIENCE_YEAR_BUFFER - index),
  );

  const titleContent = renderInlineField({
    content: (
      <Text className="whitespace-nowrap text-sm font-medium capitalize text-slate-900">
        {workExperience?.title?.trim() || "[Missing Title]"}
      </Text>
    ),
    editor: (
      <Input
        className="h-8 w-full min-w-48 px-2 text-sm font-normal text-slate-700"
        disabled={disabled}
        {...form.register("title")}
        onKeyDown={handleInputKeyDown}
      />
    ),
    field: "title",
    isContentHugging: true,
    label: "Title",
    tooltip: "Edit Title",
  });

  const companyContent = renderInlineField({
    content: (
      <Text className="whitespace-nowrap text-sm font-normal text-slate-600">
        {workExperience?.internalCompany?.name ||
          workExperience?.company ||
          "[Missing Company]"}
      </Text>
    ),
    editor: (
      <Column className="w-72 max-w-full [&_label]:hidden">
        <WorkExperienceCompanyInput
          fallbackName={workExperience?.company || undefined}
          form={form}
          isCompact
          // Creating saves only via the explicit controls.
          onSelect={isCreating ? undefined : handleSave}
        />
      </Column>
    ),
    field: "company",
    isContentHugging: true,
    label: "Company",
    tooltip: "Edit Company",
  });

  const renderDateSpinner = ({
    headerStart,
    isDisabled = false,
    month,
    monthField,
    title,
    year,
    yearField,
  }: {
    headerStart?: React.ReactNode;
    isDisabled?: boolean;
    month: string;
    monthField: "endMonth" | "startMonth";
    title: string;
    year: string;
    yearField: "endYear" | "startYear";
  }) => (
    <Column className="gap-1">
      <Row className="h-4 w-full items-center justify-between">
        <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>{title}</Text>
        {headerStart || <Row />}
      </Row>
      <Row className="items-center gap-1">
        <Select
          disabled={disabled || isDisabled}
          onValueChange={(value) => {
            form.setValue(monthField, value);
          }}
          value={month}
        >
          <SelectTrigger className="h-8 w-20 text-xs">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label.slice(0, 3)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          disabled={disabled || isDisabled}
          onValueChange={(value) => {
            form.setValue(yearField, value);
          }}
          value={year}
        >
          <SelectTrigger className="h-8 w-20 text-xs">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Row>
    </Column>
  );

  const datesContent =
    activeField === "all" || activeField === "dates-row" ? (
      <Row
        className={cn(
          "shrink-0 flex-wrap items-start gap-3",
          // Right-aligned inside the experience card; left-aligned in the add form.
          !isCreating &&
            "@2xl/work-experience:ml-auto @2xl/work-experience:justify-end @2xl/work-experience:text-right justify-start text-left",
        )}
        data-inline-editor-active
      >
        {renderDateSpinner({
          month: startMonth,
          monthField: "startMonth",
          title: "Start",
          year: startYear,
          yearField: "startYear",
        })}
        {renderDateSpinner({
          headerStart: (
            <Row className="items-center gap-1.5">
              <Checkbox
                checked={isCurrentPosition}
                className="h-3.5 w-3.5 [&_svg]:h-2.5 [&_svg]:w-2.5"
                onCheckedChange={(checked) => {
                  form.setValue("currentPosition", !!checked);
                  if (checked) {
                    form.setValue("endMonth", "");
                    form.setValue("endYear", "");
                  }
                }}
              />
              <Text className="text-xs font-normal text-slate-500">
                Current
              </Text>
            </Row>
          ),
          isDisabled: isCurrentPosition,
          month: endMonth,
          monthField: "endMonth",
          title: "End",
          year: endYear,
          yearField: "endYear",
        })}
      </Row>
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          <Column
            className="-mr-2 ml-auto shrink-0 cursor-text items-end rounded-md px-2 py-1 text-right transition-colors hover:bg-slate-100"
            onClick={() => {
              handleEditField("dates-row");
            }}
          >
            {dateDisplay ? (
              <Text className="text-sm font-normal text-slate-500">
                {dateDisplay}
              </Text>
            ) : (
              <CalendarIcon className="h-4 w-4 text-slate-400" />
            )}
            {tenure ? (
              <Text className="text-xs font-normal text-slate-500">
                {tenure}
              </Text>
            ) : null}
          </Column>
        </TooltipTrigger>
        <TooltipContent>Edit Dates</TooltipContent>
      </Tooltip>
    );

  const metaContent = renderInlineField({
    content: (
      <Row className="flex-wrap items-center gap-2 text-sm text-slate-500">
        <Text className="text-xs font-normal text-slate-500">
          {locationValue || "Location"}
        </Text>
        <Text className="text-xs font-normal text-slate-500">
          {startPayValue ? `$${String(startPayValue)}` : "Starting pay"}
        </Text>
        <Text className="text-xs font-normal text-slate-500">
          {finalPayValue ? `$${String(finalPayValue)}` : "Current/end pay"}
        </Text>
        <Text className="text-xs font-normal text-slate-500">
          {compensationTypeValue === CompensationType.HOURLY
            ? "Hourly"
            : "Salary"}
        </Text>
      </Row>
    ),
    editor: (
      <Row className="flex-wrap items-start gap-2">
        <Column className="gap-1">
          <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>
            Location
          </Text>
          {/* Hide LocationInput's own label — the kicker above replaces it. */}
          <Column className="w-44 [&_.space-y-2]:space-y-0 [&_label]:hidden">
            <LocationInput
              fieldName="location"
              formControl={form.control}
              inputClassName="h-8 w-44 px-2 text-xs"
            />
          </Column>
        </Column>
        <Column className="gap-1">
          <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>
            Starting Pay
          </Text>
          <Row className="relative items-center">
            <Text className="pointer-events-none absolute left-2 text-xs font-normal text-slate-500">
              $
            </Text>
            <Input
              className="h-8 w-28 pl-5 pr-2 text-xs"
              type="number"
              {...form.register("startPay")}
              onKeyDown={handleInputKeyDown}
            />
          </Row>
        </Column>
        <Column className="gap-1">
          <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>
            Current/End Pay
          </Text>
          <Row className="relative items-center">
            <Text className="pointer-events-none absolute left-2 text-xs font-normal text-slate-500">
              $
            </Text>
            <Input
              className="h-8 w-28 pl-5 pr-2 text-xs"
              type="number"
              {...form.register("finalPay")}
              onKeyDown={handleInputKeyDown}
            />
          </Row>
        </Column>
        <Column className="gap-1">
          <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>
            Pay Type
          </Text>
          <Select
            onValueChange={(value) => {
              form.setValue("compensationType", value as CompensationType);
            }}
            value={compensationTypeValue}
          >
            <SelectTrigger className="h-8 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {compensationTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Column>
      </Row>
    ),
    field: "meta-row",
    hasFloatingLabel: false,
    label: "Job Details",
    tooltip: "Edit Job Details",
  });

  const descriptionsContent = renderInlineField({
    content: (
      <Column className="mt-1 gap-1 pl-3">
        {(workExperience?.descriptions || []).map((description, index) => (
          <Text
            className="text-sm font-light text-[#121212] dark:text-[#FAFAFA]"
            key={index}
          >
            • {description}
          </Text>
        ))}
      </Column>
    ),
    editor: (
      <Column className="w-full min-w-0 [&_label]:hidden">
        <WorkExperienceDescriptionInput form={form} />
      </Column>
    ),
    field: "description",
    hasFloatingLabel: false,
    label: "Description",
    tooltip: "Edit Job Description",
  });

  // Create mode matches the edu/certs add forms: X to cancel, bare fields,
  // Done to save — no icon, title header, or hover controls.
  if (!workExperience) {
    return (
      <Column className="items-end" ref={editorRef}>
        <Button
          className="h-8 w-8"
          disabled={disabled}
          onClick={handleCancelCreate}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon className="h-4 w-4 text-slate-600" />
        </Button>
        <Column className="w-full gap-4">
          <TooltipProvider delayDuration={300}>
            <Form {...form}>
              {titleContent}
              {companyContent}
              {datesContent}
              {metaContent}
              {descriptionsContent}
            </Form>
          </TooltipProvider>
        </Column>
        <Row className="items-center justify-end gap-2 pb-6 pt-4">
          <Button
            disabled={disabled}
            onClick={handleCancelCreate}
            size="sm"
            type="button"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            disabled={disabled}
            onClick={handleSave}
            size="sm"
            type="button"
          >
            Save
          </Button>
        </Row>
      </Column>
    );
  }

  return (
    <Row
      className="w-full py-3 motion-safe:duration-300 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1"
      ref={editorRef}
    >
      <TooltipProvider delayDuration={300}>
        <Form {...form}>
          <WorkExperience
            companyContent={companyContent}
            datesContent={datesContent}
            descriptionsContent={descriptionsContent}
            metaContent={metaContent}
            titleContent={titleContent}
            workExperience={workExperience}
          />
        </Form>
      </TooltipProvider>
    </Row>
  );
};

const WorkExperiencesEdit = ({
  userId,
  workExperiences,
}: {
  userId: number;
  workExperiences: WorkExperienceType[];
}) => {
  const [isShowingAddForm, setIsShowingAddForm] = useState(false);

  return (
    <Column className="gap-4">
      {isShowingAddForm ? (
        <WorkExperienceEdit
          onDoneCreating={() => {
            setIsShowingAddForm(false);
          }}
          userId={userId}
        />
      ) : null}
      {workExperiences.map((workExperience) => (
        <WorkExperienceEdit
          key={workExperience.id}
          userId={userId}
          workExperience={workExperience}
        />
      ))}
      {!isShowingAddForm ? (
        <Button
          onClick={() => {
            setIsShowingAddForm(true);
          }}
          variant="outlineSecondary"
        >
          <PlusIcon className="h-4 w-4" />
          Add new work experience
        </Button>
      ) : null}
    </Column>
  );
};

export const SheetWorkExperiences = ({
  containerClasses,
  editingWorkExperienceFooter,
  editingWorkExperienceId,
  highlightKeywords,
  highlightRelevantTerms,
  isEditing,
  onEditWorkExperience,
  titleActions,
  userId,
  workExperiences,
}: {
  containerClasses?: string;
  editingWorkExperienceFooter?: ReactElement | null;
  editingWorkExperienceId?: number;
  highlightKeywords?: string;
  highlightRelevantTerms?: boolean;
  isEditing?: boolean;
  onEditWorkExperience?: (workExperienceId: number) => void;
  titleActions?: ReactElement;
  // Present when the profile is editable.
  userId?: number;
  workExperiences: WorkExperienceType[];
}) => {
  const { deleteWorkExperience } = useWorkExperience();

  const [isShowingAddForm, setIsShowingAddForm] = useState(false);

  // useWorkExperience updates the sheet-data cache at the hook level.
  const handleDeleteWorkExperience = (workExperience: WorkExperienceType) => {
    if (userId === undefined) return;

    deleteWorkExperience.mutate({ id: workExperience.id, userId });
  };

  if (isEditing && userId !== undefined) {
    return (
      <SheetResourcesWrapper
        containerClasses={containerClasses}
        isEmpty={!workExperiences.length}
        title={workExperiencesTitle}
        titleActions={titleActions}
      >
        <WorkExperiencesEdit
          userId={userId}
          workExperiences={workExperiences}
        />
      </SheetResourcesWrapper>
    );
  }

  if (editingWorkExperienceId !== undefined && userId !== undefined) {
    return (
      <SheetResourcesWrapper
        containerClasses={containerClasses}
        isEmpty={!workExperiences.length}
        title={workExperiencesTitle}
        titleActions={titleActions}
      >
        <Column className="gap-5">
          {workExperiences.map((workExperience) =>
            workExperience.id === editingWorkExperienceId ? (
              <Column className="gap-0" key={workExperience.id}>
                <WorkExperienceEdit
                  userId={userId}
                  workExperience={workExperience}
                />
                {editingWorkExperienceFooter}
              </Column>
            ) : (
              <WorkExperiencesView
                highlightKeywords={highlightKeywords}
                highlightRelevantTerms={highlightRelevantTerms}
                key={workExperience.id}
                onDeleteWorkExperience={handleDeleteWorkExperience}
                onEditWorkExperience={onEditWorkExperience}
                workExperiences={[workExperience]}
              />
            ),
          )}
        </Column>
      </SheetResourcesWrapper>
    );
  }

  // Work experiences have no section-wide edit mode, so the add button lives
  // directly on the view header whenever the profile is editable.
  const renderAddButton = () => {
    if (userId === undefined || isShowingAddForm) return null;

    return (
      <Button
        className="h-6 w-6 opacity-0 transition-opacity duration-150 group-hover/section:opacity-100 group-hover/section:delay-200"
        onClick={() => {
          setIsShowingAddForm(true);
        }}
        size="icon"
        type="button"
        variant="ghost"
      >
        <CirclePlusIcon className="h-3.5 w-3.5 text-slate-500" />
      </Button>
    );
  };

  return (
    <SheetResourcesWrapper
      containerClasses={containerClasses}
      isEmpty={!workExperiences.length}
      title={workExperiencesTitle}
      titleActions={titleActions ?? renderAddButton() ?? undefined}
    >
      <Column className="gap-4">
        {/* Hide the empty-state text while the add form is open. */}
        {isShowingAddForm && userId !== undefined ? (
          <WorkExperienceEdit
            onDoneCreating={() => {
              setIsShowingAddForm(false);
            }}
            userId={userId}
          />
        ) : null}
        {isShowingAddForm && !workExperiences.length ? null : (
          <WorkExperiencesView
            highlightKeywords={highlightKeywords}
            highlightRelevantTerms={highlightRelevantTerms}
            onDeleteWorkExperience={
              userId !== undefined ? handleDeleteWorkExperience : undefined
            }
            onEditWorkExperience={
              userId !== undefined ? onEditWorkExperience : undefined
            }
            workExperiences={workExperiences}
          />
        )}
      </Column>
    </SheetResourcesWrapper>
  );
};
